/*
 * Rang Sitaray: desktop hover / keyboard-focus behaviour for Dawn's <header-menu> disclosures.
 * Dawn only toggles these on click. This adds:
 *   - open on mouse hover, close shortly after the pointer leaves (panel included)
 *   - open on keyboard focus (focus-visible only), close when focus leaves (Dawn) or on Escape
 *   - only one menu open at a time; clicking a link closes the menu
 *   - mega panel columns aligned under the first menu item
 * Below 990px the mobile drawer is used and this script does nothing.
 * Touch devices keep Dawn's tap-to-toggle behaviour.
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const CLOSE_DELAY = 180;

  const getMenus = () => document.querySelectorAll('.header__inline-menu header-menu');

  const setOpen = (menu, open) => {
    const details = menu.querySelector('details');
    if (!details || details.open === open) return;
    details.open = open;
  };

  const closeOthers = (current) => {
    getMenus().forEach((menu) => {
      if (menu !== current) setOpen(menu, false);
    });
  };

  const alignMega = (menu) => {
    const list = menu.querySelector('.mega-menu__list');
    if (!list) return;
    const first = menu.closest('.header__inline-menu').querySelector('.header__menu-item');
    if (first) list.style.setProperty('--rs-mega-left', `${Math.round(first.getBoundingClientRect().left)}px`);
  };

  const init = (menu) => {
    if (menu.dataset.rsMenuReady) return;
    menu.dataset.rsMenuReady = 'true';

    const details = menu.querySelector('details');
    const summary = details.querySelector('summary');
    let closeTimer;

    const hoverEnabled = () => DESKTOP.matches && CAN_HOVER.matches;
    const open = () => {
      clearTimeout(closeTimer);
      closeOthers(menu);
      alignMega(menu);
      setOpen(menu, true);
    };

    summary.setAttribute('aria-expanded', details.open);
    details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', details.open));

    menu.addEventListener('mouseenter', () => {
      if (hoverEnabled()) open();
    });

    menu.addEventListener('mouseleave', () => {
      if (!hoverEnabled()) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        const active = document.activeElement;
        const keyboardInside = menu.contains(active) && active.matches(':focus-visible');
        if (!keyboardInside) setOpen(menu, false);
      }, CLOSE_DELAY);
    });

    summary.addEventListener('focus', () => {
      if (DESKTOP.matches && summary.matches(':focus-visible')) open();
    });

    // With a mouse, hover already opened the menu: a click must not toggle it shut.
    // Keyboard activation (detail === 0) and touch keep the native toggle.
    summary.addEventListener('click', (event) => {
      if (!hoverEnabled() || event.detail === 0) return;
      event.preventDefault();
      // Dawn's global.js flips aria-expanded on every summary click; restore the real state.
      setTimeout(() => summary.setAttribute('aria-expanded', details.open));
    });

    // Dropdown items with their own sub-list (nested <details>) also open on hover.
    menu.querySelectorAll('.header__submenu details').forEach((sub) => {
      sub.addEventListener('mouseenter', () => {
        if (hoverEnabled()) sub.open = true;
      });
      sub.addEventListener('mouseleave', () => {
        if (hoverEnabled()) sub.open = false;
      });
      sub.querySelector('summary').addEventListener('click', (event) => {
        if (hoverEnabled() && event.detail !== 0) event.preventDefault();
      });
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.open) {
        setOpen(menu, false);
        summary.focus();
      }
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(menu, false);
    });
  };

  const initAll = () => getMenus().forEach(init);

  DESKTOP.addEventListener('change', () => getMenus().forEach((menu) => setOpen(menu, false)));
  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
